/**
 * tableSelection.ts - Selecting cells and ranges, and keeping the spreadsheet's editing state in step
 * with the selection. Private to `createTableInteractions`.
 */

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {TryInsertOptions} from 'notebook/tables/excess/FormulaUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  computeMultiIndicesState,
  getMultiEditingText,
  getMultiEditingTypedValue,
} from 'notebook/tables/store/logic/TableMultiSelectionLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ElementMouseEvent, Optional} from 'util/Types';

export interface CellDragStartArgs {
  start: TableIndex;
  end?: TableIndex;
  active?: boolean | string;
  allowSingleOverride?: boolean;
}

export interface MultiEditingUpdate {
  index: TableIndex | null;
  editing: boolean;
  text: string | null;
  typedValue?: Optional<TypedValue>;
  selection?: BenchlingSelection | null;
}

export interface SelectRangeOptions {
  /** The anchor's text; defaults to the cell's own text. */
  text?: string;
  /** The selected cell within the range; defaults to none. */
  multiSelIndex?: TableIndex | null;
}

export function setTableMultiEditing(getTableProps: () => TableProps, e: MultiEditingUpdate): void {
  const {setMultiEditingForTable, table, documentName} = getTableProps();
  setMultiEditingForTable({tableName: table.name, documentName: documentName ?? null}, e);
}

export function tryInsertIntoTable(getTableProps: () => TableProps, options: TryInsertOptions): boolean {
  const {table, tryInsert, documentName} = getTableProps();
  const [, columnIndex] = options.index;
  // A reference to a Single Input locks its row with a $.
  if (table.columnConstraints[columnIndex]?.structuredTableColumn?.isSingleInput) {
    options.cellLock = {row: true};
  }
  return tryInsert({tableName: table.name, documentName: documentName ?? null}, options);
}

export function compareTableIndex(a: TableIndex, b: TableIndex): number {
  return a[0] - b[0] || a[1] - b[1];
}

export interface TableSelection {
  /**
   * Selects the cell at `index`. When `param.editing` is set, enters edit mode and positions the cell
   * editor over the cell. Does not commit any in-progress edit.
   */
  select: (
    index: TableIndex,
    param: MultiSelectListUtil.MultiSelectSingleParam<TableIndex> | undefined
  ) => void;
  /** Sets the selection's anchor and end, syncs the anchor's editing state and focuses the table. */
  selectRange: (start: TableIndex, end: TableIndex, options?: SelectRangeOptions) => void;
  /** Starts (or shift-extends) a selection from a mouse down. */
  beginSelection: (args: CellDragStartArgs, e: ElementMouseEvent) => void;
  /** Extends the in-progress drag selection to `index`. */
  extendSelection: (index: TableIndex) => void;
}

export function createTableSelection({
  getTableModel,
  getTableProps,
  setState,
  isMounted,
  focusBearer,
  finalizeEdit,
  positionEditor,
}: {
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
  isMounted: () => boolean;
  focusBearer: () => HTMLDivElement;
  finalizeEdit: () => void;
  positionEditor: (index: TableIndex) => void;
}): TableSelection {
  const setMultiEditing = (e: MultiEditingUpdate): void => {
    setTableMultiEditing(getTableProps, e);
  };
  const multiTryInsert = (options: TryInsertOptions): boolean => tryInsertIntoTable(getTableProps, options);
  const getText = (index: TableIndex): string => getMultiEditingText(getTableProps().table, index);
  const getTypedValue = (index: TableIndex): TypedValue | null =>
    getMultiEditingTypedValue(getTableProps().table, index);
  const isCrossEditing = (): boolean =>
    SpreadsheetStore.getState(getTableProps().tableNamespace).editingTable != null;

  return {
    select: (index, param) => {
      if (!isMounted()) {
        return;
      }
      const {state} = getTableModel();
      MultiSelectListUtil.multiSelectSingle(index, param, {
        setState,
        multiStartIndex: state.multiStartIndex,
        multiEndIndex: state.multiEndIndex,
        setMultiEditing,
        getMultiEditingText: getText,
        // When editing, remove any typed value from the spreadsheet store state to prevent it from
        // overwriting an incoming edit on finalize. This handles the case where a cell was selected and
        // a single character was typed, triggering cell edit mode.
        getMultiEditingTypedValue: param?.editing && (param?.text ?? '') !== '' ? () => null : getTypedValue,
        focusBearerNode: focusBearer(),
      });
      if (param?.editing) {
        positionEditor(index);
      }
    },

    selectRange: (start, end, options = {}) => {
      if (!isMounted()) {
        return;
      }
      setState(computeMultiIndicesState({start, end}, options.multiSelIndex ?? null));
      const editingText = options.text != null ? options.text : getText(start);
      const typedValue = getTypedValue(start);
      setMultiEditing({editing: false, index: start, text: editingText, typedValue, selection: null});
      MultiSelectListUtil._focus(focusBearer());
    },

    beginSelection: (args, e) => {
      if (!isMounted()) {
        return;
      }
      const {state, selection} = getTableModel();
      MultiSelectListUtil.multiEditableMouseDownHandler(args, e, {
        multiStartIndex: state.multiStartIndex,
        multiEndIndex: state.multiEndIndex,
        multiSelIndex: state.multiSelIndex,
        setState,
        allowSingleMultiSelect: true,
        multiTryInsert,
        multiCrossEditing: isCrossEditing(),
        multiFinalizeEditHandler: finalizeEdit,
        isIndexBetween: TableUtil.isIndexBetween,
        compareIndex: compareTableIndex,
        setMultiEditing,
        getMultiEditingText: getText,
        getMultiEditingTypedValue: getTypedValue,
        focusBearerNode: focusBearer(),
        disallowShiftExpand: !selection.hasRealSelection,
      });
    },

    extendSelection: (index) => {
      if (!isMounted()) {
        return;
      }
      const {state} = getTableModel();
      MultiSelectListUtil.multiEditableMouseOverHandler(index, {
        setState,
        multiActive: state.multiActive,
        multiStartIndex: state.multiStartIndex,
        multiEndIndex: state.multiEndIndex,
        multiExtra: state.multiExtra,
        disallowMultiExpand: false,
        multiCrossEditing: isCrossEditing(),
        allowSingleMultiSelect: true,
        multiTryInsert,
        compareIndex: compareTableIndex,
        focusBearerNode: focusBearer(),
      });
    },
  };
}
